import React, { useState, useEffect, useCallback } from 'react';
import { documentApi, projectApi, wbsApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, UploadSimple, Eye, DownloadSimple, FilePdf, FileText, Clock, CheckCircle, XCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CATEGORIES = ['drawing', 'invoice', 'inspection', 'variation', 'contract', 'RFI'];
const STATUS_MAP = { draft: 'default', submitted: 'warning', approved: 'success', rejected: 'danger' };

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ project_id: '', category: 'drawing', title: '', reference_number: '', linked_activity_id: '', linked_variation_id: '', linked_invoice_id: '' });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showVersions, setShowVersions] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionFile, setVersionFile] = useState(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [uploadingVersion, setUploadingVersion] = useState(false);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities()])
      .then(([p, a]) => { setProjects(p.data); setActivities(a.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadDocuments = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterCategory) params.category = filterCategory;
    if (filterStatus) params.status = filterStatus;
    const res = await documentApi.list(params);
    setDocuments(res.data);
  }, [filterProject, filterCategory, filterStatus]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const openCreate = () => {
    setForm({ project_id: filterProject || '', category: 'drawing', title: '', reference_number: '', linked_activity_id: '', linked_variation_id: '', linked_invoice_id: '' });
    setFile(null); setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) formData.append(k, v); });
      if (file) formData.append('file', file);
      await documentApi.create(formData);
      setShowModal(false); loadDocuments();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await documentApi.delete(id); loadDocuments(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleStatusChange = async (id, status, reason) => {
    try {
      await documentApi.changeStatus(id, { status, reason });
      loadDocuments();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const loadVersions = async (docId) => {
    if (showVersions === docId) { setShowVersions(null); return; }
    setShowVersions(docId);
    const res = await documentApi.getVersions(docId);
    setVersions(res.data);
  };

  const handleAddVersion = async (docId) => {
    if (!versionFile) return;
    setUploadingVersion(true);
    try {
      const formData = new FormData();
      formData.append('file', versionFile);
      formData.append('notes', versionNotes);
      await documentApi.addVersion(docId, formData);
      setVersionFile(null); setVersionNotes('');
      loadVersions(docId); loadDocuments();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setUploadingVersion(false); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileUrl = (filename) => `${API_URL}/api/documents/file/${filename}`;

  const getFileExt = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const isPreviewable = (filename) => {
    const ext = getFileExt(filename);
    return ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext);
  };

  const handleView = (filename) => {
    const ext = getFileExt(filename);
    const url = getFileUrl(filename);
    if (['pdf'].includes(ext)) {
      setPreviewType('pdf');
      setPreviewUrl(url);
    } else if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) {
      setPreviewType('image');
      setPreviewUrl(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const handleDownload = (filename, originalName) => {
    const url = getFileUrl(filename);
    const link = document.createElement('a');
    link.href = url;
    link.download = originalName || filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-heading)' }}>Document Management</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Upload, version, and track document approvals</p>
        </div>
        <Button onClick={openCreate} data-testid="add-document-btn"><Plus className="w-4 h-4 mr-2" /> Upload Document</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} data-testid="filter-category">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
        </Select>
        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </Select>
      </div>

      {documents.length === 0 ? (
        <Card className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>No documents found</Card>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <div key={doc.id}>
              <Card className="p-3 sm:p-4" data-testid={`document-row-${doc.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getFileExt(doc.original_filename) === 'pdf' ? (
                        <FilePdf className="w-4 h-4 text-[#DC2626]" weight="fill" />
                      ) : (
                        <FileText className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                      )}
                      <span className="font-semibold text-sm" style={{ color: 'var(--text-heading)' }}>{doc.title}</span>
                      <Badge variant={STATUS_MAP[doc.status]}>{doc.status}</Badge>
                      <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-medium" style={{ backgroundColor: 'var(--muted)', color: 'var(--text-secondary)' }}>{doc.category}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <span>{doc.reference_number}</span>
                      <span>{doc.project_name}</span>
                      <span>v{doc.version}</span>
                      {doc.original_filename && <span>{doc.original_filename} ({fmtSize(doc.file_size)})</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {doc.filename ? (
                      <>
                        {/* View Button */}
                        {isPreviewable(doc.filename) ? (
                          <button
                            onClick={() => handleView(doc.filename)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors"
                            style={{ color: 'var(--primary)', backgroundColor: 'var(--primary-light, #EFF6FF)' }}
                            title="View document"
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                        ) : (
                          <a
                            href={getFileUrl(doc.filename)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors"
                            style={{ color: 'var(--primary)', backgroundColor: 'var(--primary-light, #EFF6FF)' }}
                            title="Open document"
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="w-3.5 h-3.5" /> Open
                          </a>
                        )}

                        {/* Download Button */}
                        <button
                          onClick={() => handleDownload(doc.filename, doc.original_filename)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors"
                          style={{ color: '#059669', backgroundColor: '#ECFDF5' }}
                          title="Download document"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <DownloadSimple className="w-3.5 h-3.5" /> Download
                        </button>
                      </>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md"
                        style={{ color: 'var(--text-muted)', backgroundColor: 'var(--muted)' }}
                        data-testid={`no-file-${doc.id}`}>
                        No file attached
                      </span>
                    )}
                    <button onClick={() => loadVersions(doc.id)} className="p-1.5 rounded transition-colors hover:bg-[var(--hover-bg)]" style={{ color: 'var(--text-muted)' }} data-testid={`versions-btn-${doc.id}`}>
                      <Clock className="w-4 h-4" />
                    </button>
                    {doc.status === 'draft' && (
                      <Button size="sm" onClick={() => handleStatusChange(doc.id, 'submitted')} data-testid={`submit-doc-${doc.id}`}>Submit</Button>
                    )}
                    {doc.status === 'submitted' && (
                      <>
                        <button onClick={() => handleStatusChange(doc.id, 'approved')} className="p-1 text-[#00C853] hover:bg-[#00C853]/10 rounded" data-testid={`approve-doc-${doc.id}`}><CheckCircle className="w-5 h-5" /></button>
                        <button onClick={() => { const r = prompt('Rejection reason:'); if (r) handleStatusChange(doc.id, 'rejected', r); }} className="p-1 text-[#FF3333] hover:bg-[#FF3333]/10 rounded" data-testid={`reject-doc-${doc.id}`}><XCircle className="w-5 h-5" /></button>
                      </>
                    )}
                    {(doc.status === 'draft' || doc.status === 'rejected') && (
                      <button onClick={() => handleDelete(doc.id)} className="p-1 hover:text-[#FF3333]" style={{ color: 'var(--text-muted)' }} data-testid={`delete-doc-${doc.id}`}><Trash className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </Card>
              {showVersions === doc.id && (
                <div className="ml-4 mt-1 rounded-lg p-3 space-y-2 border" style={{ backgroundColor: 'var(--hover-bg)', borderColor: 'var(--border)' }} data-testid={`versions-panel-${doc.id}`}>
                  <p className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Version History</p>
                  {versions.map(v => (
                    <div key={v.id} className="flex items-center justify-between text-xs border-b pb-1" style={{ borderColor: 'var(--border)' }}>
                      <div style={{ color: 'var(--text-heading)' }}><span className="font-bold">v{v.version}</span> — {v.original_filename} ({fmtSize(v.file_size)})</div>
                      <div className="flex items-center gap-2">
                        <span style={{ color: 'var(--text-muted)' }}>{v.uploaded_by_name} · {v.notes}</span>
                        {v.filename && (
                          <>
                            {isPreviewable(v.filename) && (
                              <button onClick={() => handleView(v.filename)} className="text-[var(--primary)] hover:underline" data-testid={`view-version-${v.id}`}>View</button>
                            )}
                            <button onClick={() => handleDownload(v.filename, v.original_filename)} className="text-emerald-600 hover:underline" data-testid={`download-version-${v.id}`}>Download</button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <input type="file" onChange={(e) => setVersionFile(e.target.files[0])} className="text-xs" data-testid={`version-file-${doc.id}`} />
                    <Input value={versionNotes} onChange={(e) => setVersionNotes(e.target.value)} placeholder="Notes" className="flex-1" />
                    <Button size="sm" onClick={() => handleAddVersion(doc.id)} disabled={!versionFile || uploadingVersion} data-testid={`upload-version-${doc.id}`}>
                      <UploadSimple className="w-3 h-3 mr-1" /> {uploadingVersion ? '...' : 'Upload'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Document Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Upload Document">
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="document-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required data-testid="doc-project">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="doc-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </Select>
          <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="doc-title" />
          <Input label="Reference Number" value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="Auto-generated if empty" data-testid="doc-ref" />
          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--text-heading)' }}>File</label>
            <input type="file" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm" data-testid="doc-file" />
          </div>
          <Select label="Link to Activity (optional)" value={form.linked_activity_id} onChange={(e) => setForm({ ...form, linked_activity_id: e.target.value })} data-testid="doc-activity">
            <option value="">-- None --</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.activity_name}</option>)}
          </Select>
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-document-btn">{saving ? 'Uploading...' : 'Upload'}</Button>
          </div>
        </form>
      </Modal>

      {/* Document Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="document-preview-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setPreviewUrl(null); setPreviewType(null); }} />
          <div className="relative w-full max-w-5xl max-h-[90vh] rounded-xl shadow-2xl border overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
              <span className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>Document Preview</span>
              <div className="flex items-center gap-2">
                <a href={previewUrl} download className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors" data-testid="preview-download-btn">
                  <DownloadSimple className="w-3.5 h-3.5" /> Download
                </a>
                <button
                  onClick={() => { setPreviewUrl(null); setPreviewType(null); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                  style={{ backgroundColor: 'var(--muted)', color: 'var(--text-heading)' }}
                  data-testid="preview-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 56px)' }}>
              {previewType === 'pdf' ? (
                <iframe src={previewUrl} className="w-full border-0" style={{ height: 'calc(90vh - 56px)' }} title="PDF Preview" data-testid="pdf-preview-iframe" />
              ) : (
                <div className="flex items-center justify-center p-8 bg-[#F8F8F8]">
                  <img src={previewUrl} alt="Document preview" className="max-w-full max-h-[75vh] object-contain rounded" data-testid="image-preview" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
